/**
 * Mutation hooks — the write side of the IL-32 data-access seam.
 *
 * Every mutation calls the backend API, then applies the equivalent local
 * Zustand update so the UI stays consistent without a full re-fetch. The only
 * exception is the reporter's "simulate offline" toggle, which saves locally
 * and defers the sync.
 *
 * Screens import from this file, not from mutations.ts, for any action that must reach
 * the backend in production. Pure local/UI-only mutations (verify, explain gap, Heimdall
 * finding decisions, thread comments) continue to call useMutate() + mutations.ts directly
 * until backend endpoints exist for them.
 */

import { useState } from "react";
import { AxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useMutate, useActorId, useSeed } from "./hooks";
import { useStore } from "./store";
import { useOnlineStatus } from "./useOnlineStatus";
import { enqueueAction } from "./offlineQueue";
import { demoModeActive, api } from "./api";
import {
  raiseSpend, approveSpend, rejectSpend, querySpend, confirmSpend,
  addSpendItem, submitEvidence,
  approveDisbursement, queryDisbursement, holdDisbursement, routeDisbursement,
  verifySpend, setLeakState, fileDeclaration,
  kycSetAccepted, kycDecision,
  acceptFinding, dismissFinding,
  setMilestonePlan,
  setGrantThreshold,
  syncConnection, setConnectionStatus,
  createReport, approveReportDraft,
  startSiteVisit, beginVisit, setChecklistJudgement, captureVisitItem,
  completeVisit, generateVisitReportDraft, approveVisitReport,
  siteVisitChecklistLabels,
  addSiteVisitQuestion, updateSiteVisitQuestion, removeSiteVisitQuestion, syncVisitQuestions,
} from "./mutations";
import type { EvidenceItem, SpendItem, KycReview, Leak, SiteVisit, SiteVisitQuestion } from "./types";

// ---------------------------------------------------------------------------
// useRaiseSpend — IL-43
// ---------------------------------------------------------------------------

type RaiseInput = Parameters<typeof raiseSpend>[1];

export function useRaiseSpend() {
  const mutate = useMutate();
  const isOnline = useOnlineStatus();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const [isPending, setIsPending] = useState(false);
  const queryClient = useQueryClient();

  function applyLocalMutate(input: RaiseInput): string {
    let newId = "";
    mutate((s) => {
      const out = raiseSpend(s, input);
      newId = out.id;
      return out.seed;
    });
    return newId;
  }

  // `files` are the raw picked attachments (RaiseAsk.tsx keeps both a
  // base64 `documents` array for local/demo preview and the real File
  // objects for upload - see docs.map in RaiseAsk.tsx's submit()).
  // Previously these never reached the backend in real mode at all - the
  // apiPayload below had no documents field, so anything attached was
  // silently dropped.
  async function raise(input: RaiseInput, files?: File[]): Promise<string> {
    // Demo: pure local, no queue, no network - this stays a self-contained
    // walkthrough regardless of connectivity.
    if (demoModeActive()) {
      return applyLocalMutate(input);
    }

    const apiPayload = {
      projectId: input.projectId,
      subProjectId: input.subProjectId,
      milestoneId: input.milestoneId,
      title: input.title,
      description: input.purpose,
      amount: input.amount,
      category: input.category ?? "Materials",
      supplier: input.supplier,
      lineItems: input.items?.map((i) => ({
        description: i.description,
        amount: i.amount,
        category: i.category ?? "Materials",
      })),
    };

    // Genuinely offline (or the QA "simulate offline" toggle): update the UI
    // immediately via the same local mutate demo mode uses, but durably
    // queue the real payload for replay once back online - see
    // offlineQueue.ts. Unlike demo mode, this one is never meant to stay
    // local forever.
    if (offline) {
      const newId = applyLocalMutate(input);
      await enqueueAction("raiseSpend", apiPayload);
      return newId;
    }

    setIsPending(true);
    try {
      const result = await api.raiseSpend(apiPayload);
      // Mirror to local store so all reads stay consistent without a refetch.
      mutate((s) => raiseSpend(s, input).seed);
      // The Inbox's own query (client/src/screens/reporter/Inbox.tsx) has a
      // 30s staleTime - a fast raise-ask-then-navigate-back flow can land
      // well inside that window, so without this the new ask silently
      // wouldn't show up until the cache happened to go stale. Partial key
      // match invalidates regardless of the query's exact project-id list.
      queryClient.invalidateQueries({ queryKey: ["reporter-inbox"] });
      if (files?.length) {
        // Best-effort: the ask itself already exists and succeeded, so a
        // document-upload failure shouldn't read as "the whole raise
        // failed" - log it rather than throwing out of an otherwise-
        // successful raise. Offline attachments aren't queued for a later
        // retry in this round (would need the queue to chain a second
        // action off this one's server-assigned id) - a known, stated gap,
        // not a silent one.
        api.uploadSpendRequestDocuments(result.id, files).catch((e) => {
          console.warn(`Ask ${result.id} raised, but its attachments failed to upload:`, e);
        });
      }
      return String(result.id);
    } catch (e) {
      // `isOnline`/`simulatedOffline` said we were online, but the request
      // never actually reached the server (radio up, no route through -
      // captive portal, dead connection - see useOnlineStatus.ts's own
      // caveat). Fall back to the same durable-queue path the `offline`
      // branch above uses, rather than losing the raise entirely. A real
      // rejection *from* the server (validation, auth) has a `response` and
      // is left to surface as today - queuing something the server has
      // already refused would just fail again on replay.
      if (e instanceof AxiosError && !e.response) {
        const newId = applyLocalMutate(input);
        await enqueueAction("raiseSpend", apiPayload);
        return newId;
      }
      throw e;
    } finally {
      setIsPending(false);
    }
  }

  return { raise, isPending };
}

// ---------------------------------------------------------------------------
// useSpendActions — IL-40 (approve / reject / query / confirm)
// ---------------------------------------------------------------------------

export function useSpendActions() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  // `reason` is required by the compliance pack above
  // SPEND_REASON_REQUIRED_ABOVE_UGX - the drawer collects it and passes it here.
  // Optional so below-threshold approvals stay a single click.
  async function approve(id: string, reason?: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => approveSpend(s, id, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.approveSpendRequest(id, reason);
      mutate((s) => approveSpend(s, id, actorId));
    } finally {
      setIsPending(false);
    }
  }

  async function reject(id: string, reason: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => rejectSpend(s, id, actorId, reason));
      return;
    }
    setIsPending(true);
    try {
      await api.rejectSpendRequest(id, reason);
      mutate((s) => rejectSpend(s, id, actorId, reason));
    } finally {
      setIsPending(false);
    }
  }

  async function query(id: string, comment: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => querySpend(s, id, actorId, comment));
      return;
    }
    setIsPending(true);
    try {
      await api.sendBackSpendRequest(id, comment);
      mutate((s) => querySpend(s, id, actorId, comment));
    } finally {
      setIsPending(false);
    }
  }

  // Confirm transitions evidence_submitted → confirmed (IL-40 state machine).
  // Takes a reason for the same pack rule as approve above.
  async function confirm(id: string, reason?: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => confirmSpend(s, id, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.confirmSpendRequest(id, reason);
      mutate((s) => confirmSpend(s, id, actorId));
    } finally {
      setIsPending(false);
    }
  }

  return { approve, reject, query, confirm, isPending };
}

// ---------------------------------------------------------------------------
// useDisbursementActions — IL-50 / IL-51 (funder release + oversight routing)
// ---------------------------------------------------------------------------

export function useDisbursementActions() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function approve(id: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => approveDisbursement(s, id, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.approveDisbursementRequest(id);
      mutate((s) => approveDisbursement(s, id, actorId));
    } finally {
      setIsPending(false);
    }
  }

  async function query(id: string, note: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => queryDisbursement(s, id, actorId, note));
      return;
    }
    setIsPending(true);
    try {
      await api.queryDisbursementRequest(id, note);
      mutate((s) => queryDisbursement(s, id, actorId, note));
    } finally {
      setIsPending(false);
    }
  }

  async function hold(id: string, note: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => holdDisbursement(s, id, actorId, note));
      return;
    }
    setIsPending(true);
    try {
      await api.holdDisbursementRequest(id, note);
      mutate((s) => holdDisbursement(s, id, actorId, note));
    } finally {
      setIsPending(false);
    }
  }

  async function route(id: string, note?: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => routeDisbursement(s, id, actorId, note));
      return;
    }
    setIsPending(true);
    try {
      await api.routeDisbursementRequest(id, note);
      mutate((s) => routeDisbursement(s, id, actorId, note));
    } finally {
      setIsPending(false);
    }
  }

  return { approve, query, hold, route, isPending };
}

// ---------------------------------------------------------------------------
// useSubmitEvidence — IL-60
// ---------------------------------------------------------------------------

interface SubmitEvidencePayload {
  askId: string;
  spendItems: Array<Omit<SpendItem, "id" | "spendRequestId" | "committedAt" | "currency">>;
  evidence: {
    type?: EvidenceItem["type"];
    note?: string;
    geo?: { lat: number; lng: number };
    url?: string;
    filename?: string;
    /** The real picked file - uploaded for real (online) or persisted
     * durably in the offline queue for later real upload (IndexedDB stores
     * Blobs natively, no base64 needed). `url`/`filename` above stay
     * base64/local-only, used only for the immediate on-screen preview
     * regardless of connectivity. */
    file?: File;
  };
}

export function useSubmitEvidence() {
  const mutate = useMutate();
  const actorId = useActorId();
  const isOnline = useOnlineStatus();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const [isPending, setIsPending] = useState(false);

  function applyLocalMutate(payload: SubmitEvidencePayload) {
    mutate((s) => {
      let next = s;
      for (const item of payload.spendItems) {
        next = addSpendItem(next, payload.askId, item);
      }
      return submitEvidence(next, payload.askId, actorId, payload.evidence);
    });
  }

  async function submit(payload: SubmitEvidencePayload): Promise<void> {
    // Demo: pure local, no queue, no network.
    if (demoModeActive()) {
      applyLocalMutate(payload);
      return;
    }

    const apiPayload = {
      askId: payload.askId,
      type: payload.evidence.type ?? ("photo" as const),
      file: payload.evidence.file,
      note: payload.evidence.note,
      lat: payload.evidence.geo?.lat,
      lng: payload.evidence.geo?.lng,
    };

    // Genuinely offline (or the QA toggle): update the UI immediately, but
    // durably queue the real upload for replay once back online - see
    // offlineQueue.ts.
    if (offline) {
      applyLocalMutate(payload);
      await enqueueAction("submitEvidence", apiPayload);
      return;
    }

    setIsPending(true);
    try {
      await api.submitEvidence(apiPayload);
      applyLocalMutate(payload);
    } finally {
      setIsPending(false);
    }
  }

  return { submit, isPending };
}

// ---------------------------------------------------------------------------
// useVerifySpend — IL-90 assurance ladder (AssuranceView, ExceptionsView)
// ---------------------------------------------------------------------------

export function useVerifySpend() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function verify(id: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => verifySpend(s, id, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.verifySpendRequest(id);
      mutate((s) => verifySpend(s, id, actorId));
    } finally {
      setIsPending(false);
    }
  }

  return { verify, isPending };
}

// ---------------------------------------------------------------------------
// useLeakActions — IL-91 signals inbox (LeaksInbox)
// ---------------------------------------------------------------------------

export function useLeakActions() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function setState(id: string, state: Leak["state"], note?: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => setLeakState(s, id, state, actorId, note));
      return;
    }
    setIsPending(true);
    try {
      await api.setLeakState(id, state, note);
      mutate((s) => setLeakState(s, id, state, actorId, note));
    } finally {
      setIsPending(false);
    }
  }

  return { setState, isPending };
}

// ---------------------------------------------------------------------------
// useFileDeclaration — IL-92 compliance (ComplianceView)
// ---------------------------------------------------------------------------

export function useFileDeclaration() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function file(id: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => fileDeclaration(s, id, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.fileDeclaration(id);
      mutate((s) => fileDeclaration(s, id, actorId));
    } finally {
      setIsPending(false);
    }
  }

  return { file, isPending };
}

// ---------------------------------------------------------------------------
// useKycActions — IL-93 KYC pipeline (KycDetailDrawer)
// ---------------------------------------------------------------------------

export function useKycActions() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  // Synchronous local toggle — marks a document as accepted/not (enables Approve).
  // Backend call included so acceptance is persisted in production.
  function setAccepted(reviewId: string, docKey: string, accepted: boolean): void {
    mutate((s) => kycSetAccepted(s, reviewId, docKey, accepted));
    if (!demoModeActive()) {
      api.kycSetDocAccepted(reviewId, docKey, accepted).catch(() => {
        // Rollback on failure.
        mutate((s) => kycSetAccepted(s, reviewId, docKey, !accepted));
      });
    }
  }

  async function decide(reviewId: string, decision: KycReview["state"], note?: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => kycDecision(s, reviewId, actorId, decision, note));
      return;
    }
    setIsPending(true);
    try {
      await api.kycDecide(reviewId, decision, note);
      mutate((s) => kycDecision(s, reviewId, actorId, decision, note));
    } finally {
      setIsPending(false);
    }
  }

  return { setAccepted, decide, isPending };
}

// ---------------------------------------------------------------------------
// useAiFindingActions — IL-91 Heimdall queue (HeimdallQueue)
// ---------------------------------------------------------------------------

export function useAiFindingActions() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function accept(id: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => acceptFinding(s, id, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.acceptAiFinding(id);
      mutate((s) => acceptFinding(s, id, actorId));
    } finally {
      setIsPending(false);
    }
  }

  async function dismiss(id: string, reason: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => dismissFinding(s, id, actorId, reason));
      return;
    }
    setIsPending(true);
    try {
      await api.dismissAiFinding(id, reason);
      mutate((s) => dismissFinding(s, id, actorId, reason));
    } finally {
      setIsPending(false);
    }
  }

  return { accept, dismiss, isPending };
}

// ---------------------------------------------------------------------------
// useUpdateMilestone — IL-100 planning surface
// ---------------------------------------------------------------------------

type MilestonePatch = { targetDate?: string; plannedBudget?: number; evidenceRequirements?: string[] };

export function useUpdateMilestone() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function update(milestoneId: string, patch: MilestonePatch): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => setMilestonePlan(s, milestoneId, patch, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.updateMilestone(milestoneId, patch);
      mutate((s) => setMilestonePlan(s, milestoneId, patch, actorId));
    } finally {
      setIsPending(false);
    }
  }

  return { update, isPending };
}

// ---------------------------------------------------------------------------
// useUpdateGrantThreshold — IL-94 spending authority delegation
// ---------------------------------------------------------------------------

export function useUpdateGrantThreshold() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function updateThreshold(grantId: string, threshold: number): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => setGrantThreshold(s, grantId, threshold, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.updateGrantThreshold(grantId, threshold);
      mutate((s) => setGrantThreshold(s, grantId, threshold, actorId));
    } finally {
      setIsPending(false);
    }
  }

  return { updateThreshold, isPending };
}

// ---------------------------------------------------------------------------
// useConnectionActions — IL-102 connections (sync + status)
//
// importMobileMoneyStatement and reconcileFeedTxn have no backend endpoints
// yet (feed_transaction table not built) — those stay as direct useMutate
// calls in ConnectionsPage until the reconciliation backend is in place.
// ---------------------------------------------------------------------------

export function useConnectionActions() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function sync(connectionId: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => syncConnection(s, connectionId));
      return;
    }
    setIsPending(true);
    try {
      await api.syncAdapter(connectionId);
      mutate((s) => syncConnection(s, connectionId));
    } finally {
      setIsPending(false);
    }
  }

  async function setStatus(connectionId: string, status: "connected" | "not_connected"): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => setConnectionStatus(s, connectionId, status, actorId));
      return;
    }
    setIsPending(true);
    try {
      // Backend uses "active"/"paused"; "not_connected" maps to paused.
      await api.setAdapterStatus(connectionId, status === "connected" ? "active" : "paused");
      mutate((s) => setConnectionStatus(s, connectionId, status, actorId));
    } finally {
      setIsPending(false);
    }
  }

  return { sync, setStatus, isPending };
}

// ---------------------------------------------------------------------------
// useCreateReport — IL-103 reports gallery
// ---------------------------------------------------------------------------

type CreateReportParams = Parameters<typeof createReport>[1];

export function useCreateReport() {
  const mutate = useMutate();
  const seed = useSeed();
  const [isPending, setIsPending] = useState(false);

  async function create(params: CreateReportParams): Promise<string> {
    if (demoModeActive()) {
      let newId = "";
      mutate((s) => {
        const res = createReport(s, params);
        newId = res.id;
        return res.seed;
      });
      return newId;
    }

    setIsPending(true);
    try {
      // Compose sections from the current seed (same logic as demo mode) so the
      // backend stores meaningful narrative. Use the seed already read at hook level.
      const sections = createReport(seed, params).seed.reports?.[0]?.sections ?? [];

      const result = await api.createReport({
        programmeid: seed.programme.id,
        title: params.title,
        type: params.type,
        scope: params.scope,
        projectid: params.projectId ?? null,
        audience: params.audience,
        periodlabel: params.periodLabel,
        periodstart: params.periodStart,
        periodend: params.periodEnd,
        sections,
        draftedbyai: params.draftedByAI ?? false,
        limiteddata: seed.spendRequests.length < 3,
        visibleto: params.visibleTo ?? [],
        notes: params.notes,
      });

      // Mirror to Zustand with the server-assigned ID.
      mutate((s) => createReport(s, params, result.id).seed);
      return result.id;
    } finally {
      setIsPending(false);
    }
  }

  return { create, isPending };
}

// ---------------------------------------------------------------------------
// useApproveReport — IL-103 report review + file
// ---------------------------------------------------------------------------

export function useApproveReport() {
  const mutate = useMutate();
  const actorId = useActorId();
  const [isPending, setIsPending] = useState(false);

  async function approve(reportId: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => approveReportDraft(s, reportId, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.approveReport(reportId);
      mutate((s) => approveReportDraft(s, reportId, actorId));
    } finally {
      setIsPending(false);
    }
  }

  return { approve, isPending };
}

// ---------------------------------------------------------------------------
// Site visit hooks — IL SiteVisitsList + SiteVisitView
// ---------------------------------------------------------------------------

type StartVisitInput = Parameters<typeof startSiteVisit>[1];

export function useStartSiteVisit() {
  const mutate = useMutate();
  const offline = useStore((s) => s.reporterOffline);
  const seed = useSeed();
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);

  async function start(input: StartVisitInput): Promise<string> {
    if (demoModeActive() || offline) {
      let newId = "";
      mutate((s) => {
        const out = startSiteVisit(s, input);
        newId = out.id;
        return out.seed;
      });
      return newId;
    }

    setIsPending(true);
    try {
      // Build the checklist from the oversight-managed question template. In real
      // mode the custom questions live in the backend (fetched into the query
      // cache), not the static seed blob — pass them through as the override.
      const cachedQuestions = queryClient.getQueryData<SiteVisitQuestion[]>(["site-visit-questions", seed.programme.id]);
      const reqs: string[] = siteVisitChecklistLabels(seed, cachedQuestions);
      const checklist = reqs.map((label, i) => ({
        id: `${input.projectId}-c${i}`,
        label,
        judgement: "unverified" as const,
        captureIds: [] as string[],
      }));

      const result = await api.startSiteVisit({
        programmeid: seed.programme.id,
        projectid: input.projectId ?? null,
        milestoneid: null,
        checklist,
      });

      // Invalidate the TanStack Query cache so the detail page sees the new visit.
      await queryClient.invalidateQueries({ queryKey: ["site-visits"] });
      // Re-build with the server-assigned UUID so IDs stay in sync.
      mutate((s) => startSiteVisit(s, input, result.id).seed);
      return result.id;
    } finally {
      setIsPending(false);
    }
  }

  return { start, isPending };
}

// Refetch the site-visit list/detail from the API so a lifecycle change (begin,
// complete, report) shows immediately after a mutation. Real mode reads visits
// from this query; in demo mode there is no query and the invalidate is a no-op.
function useInvalidateSiteVisits() {
  const queryClient = useQueryClient();
  const programmeSlug = useSeed()?.programme.id;
  return () => queryClient.invalidateQueries({ queryKey: ["site-visits", programmeSlug] });
}

export function useBeginVisit() {
  const mutate = useMutate();
  const invalidateVisits = useInvalidateSiteVisits();
  const isOnline = useOnlineStatus();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const [isPending, setIsPending] = useState(false);

  async function begin(visitId: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => beginVisit(s, visitId));
      return;
    }

    // Previously this had no offline branch at all - a real disconnect just
    // threw out of api.beginSiteVisit with nothing catching it. Now it
    // durably queues like raise-ask/evidence instead.
    if (offline) {
      mutate((s) => beginVisit(s, visitId));
      await enqueueAction("siteVisitBegin", { visitId });
      return;
    }

    setIsPending(true);
    try {
      await api.beginSiteVisit(visitId);
      mutate((s) => beginVisit(s, visitId));
      await invalidateVisits();
    } finally {
      setIsPending(false);
    }
  }

  return { begin, isPending };
}

// Checklist judgements and captures are high-frequency field operations.
// Always update optimistically so the UI never blocks. What happens to the
// backend call depends on connectivity:
//  - online: fire it in the background, reconciling via invalidate. A
//    failure here is rare (server-side rejection) and was already
//    best-effort before this change - still not queued, since retrying a
//    live, connected request automatically risks masking a real error.
//  - offline (real disconnect, not just demo): durably enqueue it instead
//    of firing-and-swallowing. Previously a genuine disconnect during a
//    visit (this hook's apiBacked check only ever looked at the reporter's
//    manual "simulate offline" toggle, which the stratus persona running
//    site visits has no UI for at all) went straight into a fire-and-forget
//    call with `.catch(() => {})` - a real failure was discarded completely,
//    with no queue, no retry, and no visible sign anything was lost.
//
// Demo mode reads the visit from the zustand store, so we mutate that.
// Real (online or queued-offline) mode reads it from the
// ["site-visits", programme] query cache, so we patch that cache
// optimistically and invalidate to reconcile with the DB once synced.
export function useChecklistActions(visitId: string) {
  const mutate = useMutate();
  const isOnline = useOnlineStatus();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const queryClient = useQueryClient();
  const programmeSlug = useSeed()?.programme.id;
  const demo = demoModeActive();
  const visitsKey = ["site-visits", programmeSlug];

  // Patch the raw API rows held in the query cache so the change shows immediately.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function patchCachedVisit(patch: (row: any) => any): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryClient.setQueryData<any[]>(visitsKey, (rows) =>
      Array.isArray(rows) ? rows.map((r) => (String(r.id) === visitId ? patch(r) : r)) : rows,
    );
  }

  function judge(itemId: string, judgement: SiteVisit["checklist"][number]["judgement"]): void {
    if (demo) {
      mutate((s) => setChecklistJudgement(s, visitId, itemId, judgement));
      return;
    }
    patchCachedVisit((r) => ({
      ...r,
      checklist: (r.checklist ?? []).map((c: { id: string }) => (c.id === itemId ? { ...c, judgement } : c)),
    }));
    if (offline) {
      void enqueueAction("siteVisitJudge", { visitId, itemId, judgement });
      return;
    }
    api.setChecklistJudgement(visitId, itemId, judgement)
      .catch(() => {})
      .finally(() => queryClient.invalidateQueries({ queryKey: visitsKey }));
  }

  function capture(input: { type: "photo" | "note"; checklistItemId?: string; note?: string; url?: string; file?: File }): void {
    if (demo) {
      mutate((s) => captureVisitItem(s, visitId, input));
      return;
    }
    // Optimistically append the capture (temp id) and link it to its checklist item;
    // the invalidate below swaps in the server-assigned id/geo on reconcile.
    const tempId = `tmp-${Date.now()}`;
    patchCachedVisit((r) => {
      const capture = {
        id: tempId,
        type: input.type,
        takenAt: new Date().toISOString(),
        note: input.note ?? null,
        url: input.url ?? null,
        linkedChecklistItemId: input.checklistItemId ?? null,
      };
      const checklist = (r.checklist ?? []).map((c: { id: string; captureIds?: string[] }) =>
        c.id === input.checklistItemId ? { ...c, captureIds: [...(c.captureIds ?? []), tempId] } : c,
      );
      return { ...r, captures: [...(r.captures ?? []), capture], checklist };
    });
    if (offline) {
      // IndexedDB stores the real File natively (see offlineQueue.ts) - the
      // photo itself is queued for real upload on sync, not just its name.
      void enqueueAction("siteVisitCapture", { visitId, input });
      return;
    }
    api.addVisitCapture(visitId, input)
      .catch(() => {})
      .finally(() => queryClient.invalidateQueries({ queryKey: visitsKey }));
  }

  return { judge, capture };
}

// Oversight-managed site-visit question template. Demo/session-local: edits mutate
// the workspace working copy (like every other demo mutation). In real mode the
// edits hit the backend (site_visit_question) and the query is invalidated so the
// list refetches.
export function useSiteVisitQuestionActions() {
  const mutate = useMutate();
  const actorId = useActorId();
  const seed = useSeed();
  const queryClient = useQueryClient();
  const programmeSlug = seed?.programme.id;

  async function invalidate() {
    await queryClient.invalidateQueries({ queryKey: ["site-visit-questions", programmeSlug] });
  }

  async function add(label: string): Promise<void> {
    if (demoModeActive()) { mutate((s) => addSiteVisitQuestion(s, label, actorId)); return; }
    if (!programmeSlug) return;
    await api.addSiteVisitQuestion({ programmeid: programmeSlug, label: label.trim() });
    await invalidate();
  }
  async function update(id: string, label: string): Promise<void> {
    if (demoModeActive()) { mutate((s) => updateSiteVisitQuestion(s, id, label, actorId)); return; }
    await api.updateSiteVisitQuestion(id, label.trim());
    await invalidate();
  }
  async function remove(id: string): Promise<void> {
    if (demoModeActive()) { mutate((s) => removeSiteVisitQuestion(s, id, actorId)); return; }
    await api.removeSiteVisitQuestion(id);
    await invalidate();
  }

  return { add, update, remove };
}

// Append newly-added template questions to an in-progress visit without disturbing
// recorded answers. Completed visits are frozen (the mutation refuses).
export function useSyncVisitQuestions() {
  const mutate = useMutate();
  const seed = useSeed();
  const queryClient = useQueryClient();
  const programmeSlug = seed?.programme.id;

  async function sync(visitId: string): Promise<void> {
    if (demoModeActive()) { mutate((s) => syncVisitQuestions(s, visitId)); return; }
    await api.syncVisitQuestions(visitId);
    await queryClient.invalidateQueries({ queryKey: ["site-visits", programmeSlug] });
  }
  return { sync };
}

export function useCompleteVisit() {
  const mutate = useMutate();
  const invalidateVisits = useInvalidateSiteVisits();
  const isOnline = useOnlineStatus();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const [isPending, setIsPending] = useState(false);

  async function complete(visitId: string, summary?: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => completeVisit(s, visitId, summary));
      return;
    }

    // Previously had no offline branch - the "leave the site" moment is
    // exactly when connectivity is most likely to be poor, and a failure
    // here just threw with nothing catching it (SiteVisitView's button
    // handler awaits this unguarded).
    if (offline) {
      mutate((s) => completeVisit(s, visitId, summary));
      await enqueueAction("siteVisitComplete", { visitId, summary });
      return;
    }

    setIsPending(true);
    try {
      await api.completeSiteVisit(visitId, summary);
      mutate((s) => completeVisit(s, visitId, summary));
      await invalidateVisits();
    } finally {
      setIsPending(false);
    }
  }

  return { complete, isPending };
}

export function useGenerateVisitReport() {
  const mutate = useMutate();
  const actorId = useActorId();
  const seed = useSeed();
  const invalidateVisits = useInvalidateSiteVisits();
  const [isPending, setIsPending] = useState(false);

  async function generate(visitId: string): Promise<string> {
    if (demoModeActive()) {
      let reportId = "";
      mutate((s) => {
        const out = generateVisitReportDraft(s, visitId, actorId);
        reportId = out.reportId;
        return out.seed;
      });
      return reportId;
    }

    setIsPending(true);
    try {
      // Extract sections from the local mutation result, then POST to backend.
      const localOut = generateVisitReportDraft(seed, visitId, actorId);
      const sections = localOut.seed.reports?.[0]?.sections ?? [];

      const visit = seed.siteVisits?.find((v) => v.id === visitId);
      const project = seed.projects.find((p) => p.id === visit?.projectId);

      const result = await api.generateVisitReport(visitId, {
        title: `Site visit · ${project?.name ?? "project"}`,
        programmeid: seed.programme.id,
        sections,
      });

      mutate((s) => generateVisitReportDraft(s, visitId, actorId, result.reportId).seed);
      await invalidateVisits();
      return result.reportId;
    } finally {
      setIsPending(false);
    }
  }

  return { generate, isPending };
}

export function useApproveVisitReport() {
  const mutate = useMutate();
  const actorId = useActorId();
  const invalidateVisits = useInvalidateSiteVisits();
  const [isPending, setIsPending] = useState(false);

  async function approve(visitId: string): Promise<void> {
    if (demoModeActive()) {
      mutate((s) => approveVisitReport(s, visitId, actorId));
      return;
    }
    setIsPending(true);
    try {
      await api.approveVisitReport(visitId);
      // Mirror locally — finding creation is demo-only until ai_finding table is live.
      mutate((s) => approveVisitReport(s, visitId, actorId));
      await invalidateVisits();
    } finally {
      setIsPending(false);
    }
  }

  return { approve, isPending };
}
